import React, { useState, useRef, useMemo } from 'react'
import moment, { Moment } from 'moment-timezone'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { useTenantTimezone } from '../shared/contexts'

interface WeekPickerProps {
  selectedDate: Moment
  onDateChange: (date: Moment) => void
  onPreviousWeek: () => void
  onNextWeek: () => void
}

const WeekPicker = ({ selectedDate, onDateChange, onPreviousWeek, onNextWeek }: WeekPickerProps) => {
  const tenantTz = useTenantTimezone()
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const datePickerButtonRef = useRef<HTMLButtonElement>(null)
  const today = moment.tz(tenantTz)

  const isCurrentWeek = useMemo(() => {
    return selectedDate.isSame(today, 'isoWeek')
  }, [selectedDate])

  const weekRangeText = useMemo(() => {
    if (isCurrentWeek) {
      return 'This week'
    }
    const startOfWeek = selectedDate.clone().startOf('isoWeek')
    const endOfWeek = selectedDate.clone().endOf('isoWeek')

    if (startOfWeek.month() === endOfWeek.month()) {
      return `${startOfWeek.format('MMM D')} - ${endOfWeek.format('D')}`
    }

    return `${startOfWeek.format('MMM D')} - ${endOfWeek.format('MMM D')}`
  }, [selectedDate, isCurrentWeek])

  const handleDatePickerChange = (newDate: Moment | null) => {
    if (newDate) {
      onDateChange(newDate)
    }
  }

  return (
    <div className="menu-week-navigation">
      <div className="menu-navigation">
        <button className="nav-arrow-btn previous" onClick={onPreviousWeek}>
          <i className="fa fa-chevron-left" />
        </button>
        <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
          <button
            ref={datePickerButtonRef}
            className="nav-week-btn"
            onClick={() => setDatePickerOpen(true)}
          >
            {weekRangeText}
          </button>
          <DesktopDatePicker
            open={datePickerOpen}
            onOpen={() => setDatePickerOpen(true)}
            onClose={() => setDatePickerOpen(false)}
            value={selectedDate}
            onChange={handleDatePickerChange}
            sx={{ display: 'none' }}
            slotProps={{
              popper: {
                anchorEl: datePickerButtonRef.current,
                placement: 'bottom-start',
              }
            }}
          />
        </LocalizationProvider>
        <button className="nav-arrow-btn next" onClick={onNextWeek}>
          <i className="fa fa-chevron-right" />
        </button>
      </div>
    </div>
  )
}

export default WeekPicker
